import type { ProBrowseCard } from "../../lib/marketplace-discovery-api";

/**
 * Plan 435 M3 (mfh-agent-preview). First `limit` browse agents sorted by
 * `estimated_savings.cents` desc (tech-spec Concepts "Tier-matched
 * preview" / design §4.1) — browse itself is already tier-filtered
 * server-side (SILL-512), so sorting here never widens who's eligible, it
 * only orders the carrot. Agents with no `estimated_savings` sort last
 * (never fabricate a 0 that would outrank a real figure — they're treated
 * as "lowest" via `-Infinity`, not 0, so a genuine $0 savings figure still
 * outranks "no figure at all").
 */
export function topAgentsBySavings(
  agents: readonly ProBrowseCard[],
  limit = 3,
): ProBrowseCard[] {
  return [...agents]
    .sort((a, b) => savingsCents(b) - savingsCents(a))
    .slice(0, limit);
}

function savingsCents(agent: ProBrowseCard): number {
  return agent.estimated_savings?.cents ?? Number.NEGATIVE_INFINITY;
}

export type AgentPreviewSectionState = "unpublished" | "live" | "waiting";

/**
 * Section label per design §4.1 / roadmap mfh-agent-preview: pre-publish
 * names the tier, live is tier-agnostic, and "waiting" (a pending request
 * in flight) frames the rest of the list as alternatives.
 */
export function agentPreviewSectionLabel(
  state: AgentPreviewSectionState,
  area: string,
  tier?: string,
): string {
  const areaSuffix = area ? ` · ${area}` : "";
  if (state === "unpublished") {
    const tierLabel = tier ? tier.toUpperCase() : "a";
    return `Agents with ${tierLabel === "a" ? "an" : `a ${tierLabel}`} offer for you${areaSuffix}`;
  }
  if (state === "waiting") {
    return `Other agents serving ${area || "your area"}`;
  }
  return `Agents serving ${area || "your area"}`;
}
